const BRU_REQUEST_BLOCK = /^(get|post|put|patch|delete|head|options|trace|connect|http|grpc|ws)\s*\{/m;

/** A `.bru` text that is a REQUEST (has a method / grpc / ws block), not a folder.bru or env. */
export const isBruRequestText = (text) => BRU_REQUEST_BLOCK.test(String(text || ''));
const BRU_PART_BLOCK = /^\s*(meta|vars|vars:secret|vars:pre-request|headers|auth|auth:[\w-]+|script:[\w-]+|tests|docs)\s*[{[]/;

const isHoppCollection = (c) =>
  !!c && typeof c === 'object' && !Array.isArray(c) && typeof c.name === 'string' &&
  Array.isArray(c.folders) && Array.isArray(c.requests);

const isHoppEnv = (e) =>
  !!e && typeof e === 'object' && !Array.isArray(e) && typeof e.name === 'string' &&
  !('requests' in e) && Array.isArray(e.variables) &&
  e.variables.every((v) => v && typeof v.key === 'string' &&
    ('initialValue' in v || 'currentValue' in v || 'value' in v));

/**
 * @param {unknown} data Parsed file data (JSON/YAML object, or raw text for Bruno)
 * @returns {'apiboo'|'postman'|'insomnia'|'openapi3'|'swagger2'|'har'|'bruno'|'bruno_yml'|'bruno_json'|'opencollection'|'bruno_part'|'hoppscotch'|'hoppscotch_env'|'postman_env'|'unknown'}
 */
export function detectImportFormat(data) {
  // Bruno: raw .bru text. A request file has a method block; `folder.bru`,
  // `collection.bru` and environment files are parts of a collection FOLDER.
  if (typeof data === 'string') {
    // (A meta-led file stays 'bruno' — parseImportFile refuses a folder.bru with the specific message.)
    if (/^\s*meta\s*\{/.test(data)) return 'bruno';
    if (BRU_PART_BLOCK.test(data)) return 'bruno_part';
    return 'unknown';
  }

  // Hoppscotch exports "all collections" / "all environments" as an ARRAY.
  if (Array.isArray(data)) {
    if (data.length && data.every(isHoppCollection)) return 'hoppscotch';
    if (data.length && data.every(isHoppEnv)) return 'hoppscotch_env';
    return 'unknown';
  }

  if (!data || typeof data !== 'object') return 'unknown';

  // Apiboo native envelope — checked FIRST among the object shapes: it is the
  // only one carrying an explicit self-identifying marker, so nothing else can
  // legitimately match it, and no heuristic below should get the chance to
  // misclaim it.
  if (data.apiboo && data.apiboo.format === 'collection') return 'apiboo';

  // HAR: JSON with log.entries array
  if (Array.isArray(data?.log?.entries)) return 'har';

  if (typeof data.openapi === 'string' && data.openapi.startsWith('3')) {
    return 'openapi3';
  }
  if (data.swagger === '2.0' || data.swagger === 2) {
    return 'swagger2';
  }

  if (
    data.__export_format != null ||
    (data._type === 'export' && Array.isArray(data.resources))
  ) {
    return 'insomnia';
  }

  if (Array.isArray(data.item) && data.info && typeof data.info === 'object') {
    return 'postman';
  }

  if (data.info?.schema && String(data.info.schema).includes('collection')) {
    return 'postman';
  }

  if (data.collection && Array.isArray(data.collection.item)) {
    return 'postman';
  }

  // Bruno (OpenCollection YAML, default since Bruno 3.1). A bundled export has
  // its items inline; a bare `opencollection.yml` (no items, not bundled) is
  // the ROOT file of a collection folder.
  if (typeof data.opencollection === 'string') {
    return (Array.isArray(data.items) && data.items.length) || data.bundled === true ? 'opencollection' : 'bruno_part';
  }
  if (data.info && typeof data.info === 'object' && typeof data.info.type === 'string') {
    const t = data.info.type.toLowerCase();
    if ((t === 'http' && data.http && typeof data.http === 'object') ||
        (t === 'graphql' && data.graphql && typeof data.graphql === 'object')) return 'bruno_yml';
    if (t === 'folder') return 'bruno_part';
  }

  // Bruno → Export → Bruno collection (JSON): Bruno's own model.
  if (Array.isArray(data.items) && typeof data.name === 'string' &&
      (data.brunoConfig || /^Bruno/.test(String(data.exportedUsing || '')) ||
       ['http', 'graphql', 'folder'].includes(data.items[0]?.type))) {
    return 'bruno_json';
  }
  // `bruno.json` alone: the manifest of a collection folder.
  if (data.type === 'collection' && typeof data.name === 'string' && data.version != null && !('items' in data)) {
    return 'bruno_part';
  }

  if (isHoppCollection(data)) return 'hoppscotch';

  // Postman ENVIRONMENT export. Checked LAST on purpose: every collection /
  // spec / archive shape above has already claimed its file, so this cannot
  // shadow any of them. The explicit scope marker is trusted outright; the
  // marker-less shape (files from current Postman builds omit the marker)
  // needs a non-empty `name` plus a `values` array whose entries all carry
  // string keys.
  if (
    data._postman_variable_scope === 'environment' ||
    (Array.isArray(data.values) &&
      typeof data.name === 'string' &&
      data.name.trim() !== '' &&
      data.values.every((v) => v && typeof v.key === 'string'))
  ) {
    return 'postman_env';
  }

  // Hoppscotch environment: `variables` of `{key, initialValue|currentValue|value}`, no requests.
  // After postman_env (which needs `values`), so neither can shadow the other.
  if (isHoppEnv(data)) return 'hoppscotch_env';

  return 'unknown';
}

export function formatLabel(format) {
  switch (format) {
    case 'apiboo':
      return 'Apiboo Collection (v1)';
    case 'postman':
      return 'Postman Collection';
    case 'insomnia':
      return 'Insomnia Export';
    case 'openapi3':
      return 'OpenAPI 3.x';
    case 'swagger2':
      return 'Swagger 2.0';
    case 'har':
      return 'HAR (HTTP Archive)';
    case 'bruno':
      return 'Bruno (.bru)';
    case 'bruno_yml':
      return 'Bruno request (.yml)';
    case 'bruno_json':
      return 'Bruno collection (JSON export)';
    case 'opencollection':
      return 'Bruno collection (OpenCollection YAML)';
    case 'bruno_folder':
      return 'Bruno collection (folder)';
    case 'hoppscotch':
      return 'Hoppscotch collection';
    case 'hoppscotch_env':
      return 'Hoppscotch environment';
    case 'postman_env':
      return 'Postman Environment';
    default:
      return 'Unknown';
  }
}
